import { AnimatedGaugeChart } from '@/components/ui/AnimatedGaugeChart'
import { MarketIndicatorBaseProps } from './MarketIndicatorBase'

export const MarketIndicatorDesktop = ({ 
  buyerPercentage = 75,
  sentiment = 'Greed',
  priceSpread = 8.2,
  dealSpread = 51,
  loading = false
}: MarketIndicatorBaseProps) => {
  if (loading) {
    return (
      <div className="h-full flex flex-col">
        <p 
          className="font-plus-jakarta font-medium leading-[24px] text-[#2d2923] dark:text-[#fdfdfd] text-base uppercase"
          
        >
          Market indicator
        </p>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)] text-sm">
            Loading market data...
          </div>
        </div>
      </div>
    )
  }

  // Calculate progress bar width (clamped to 100%)
  const priceSpreadWidth = Math.min(Math.abs(priceSpread), 100)
  const dealSpreadWidth = Math.min(Math.abs(dealSpread), 100)

  return (
    <div className="h-full relative w-full">
      {/* Header - positioned exactly as in Figma: x=24, y=24 */}
      <p 
        className="font-plus-jakarta font-medium leading-[24px] text-[#2d2923] dark:text-[#fdfdfd] text-base uppercase"
        
      >
        Market indicator
      </p>

      {/* Gauge - positioned exactly as in Figma: x=24, y=88 */}
      <div className="absolute top-[64px] left-0 w-[340px] h-[340px]">
        <AnimatedGaugeChart
          value={buyerPercentage}
          max={100}
          min={0}
          gaugePrimaryColor="#C4E804"
          gaugeSecondaryColor="#ffffff"
          className="w-full h-full"
        />
      </div>

      {/* Buyer label - aligned with left edge of gauge arc */}
      <p 
        className="absolute top-[250px] left-0 font-plus-jakarta font-medium leading-[20px] text-[#2d2923] dark:text-[#fdfdfd] text-sm"
        
      >
        Buyer
      </p>

      {/* Seller label - aligned with right edge of gauge arc */}
      <p 
        className="absolute top-[250px] left-[180px] font-plus-jakarta font-medium leading-[20px] text-[#2d2923] dark:text-[#fdfdfd] text-sm"
        
      >
        Seller
      </p>

      {/* Description - positioned exactly as in Figma: x=290, y=104, width=153 */}
      <p 
        className="absolute top-[80px] left-[266px] font-plus-jakarta font-normal leading-[24px] text-[#2d2923] dark:text-[rgba(253,253,253,0.72)] text-base w-[153px] whitespace-pre-wrap"
        
      >
        Market sentiment indicator for buyers/sellers ratio
      </p>

      {/* Right: Spread Cards Container - positioned exactly as in Figma: x=570, y=72, width=395 */}
      <div className="absolute top-[48px] right-0 w-[395px] flex flex-col gap-2">
        {/* Price Spread Card */}
        <div className="bg-white dark:bg-[#1c1c1c] flex flex-col gap-2 p-3">
          <p 
            className="font-plus-jakarta font-semibold leading-[18px] text-[#2d2923] dark:text-[#fdfdfd] text-sm"
            
          >
            Price Spread
          </p>
          <div className="flex flex-col gap-1">
            <div className="flex items-center justify-between">
              <p 
                className="font-plus-jakarta font-medium leading-6 text-[#2d2923] dark:text-[#fdfdfd] tracking-[-0.2px]"
                
              >
                <span className="text-[18px]">{Math.abs(priceSpread).toFixed(1)} </span>
                <span className="text-[12px] text-[rgba(45,41,35,0.48)] dark:text-[rgba(253,253,253,0.48)]">%</span>
              </p>
              <div className="h-[28px] overflow-hidden relative w-[172px]">
                {/* Background pattern - using repeating pattern */}
                <div 
                  className="absolute h-5 right-0 top-1 w-[177px] bg-[rgba(45,41,35,0.05)] dark:bg-[rgba(253,253,253,0.05)]" 
                  style={{
                    backgroundImage: 'repeating-linear-gradient(90deg, transparent, transparent 1px, rgba(45,41,35,0.15) 1px, rgba(45,41,35,0.15) 2px)'
                  }}
                />
                {/* Progress bar */}
                <div 
                  className="absolute bg-[#e3fd5a] h-5 left-0 top-1"
                  style={{ width: `${priceSpreadWidth}%` }}
                />
              </div>
            </div>
            <p 
              className="font-plus-jakarta font-normal leading-3 text-[11px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)] whitespace-pre-wrap"
              
            >
              Difference between buy and sell prices
            </p>
          </div>
        </div>

        {/* Deal Size-Spread Card */}
        <div className="bg-white dark:bg-[#1c1c1c] flex flex-col gap-2 p-3">
          <p 
            className="font-plus-jakarta font-semibold leading-[18px] text-[#2d2923] dark:text-[#fdfdfd] text-sm"
            
          >
            Deal Size-Spread
          </p>
          <div className="flex flex-col gap-1">
            <div className="flex items-center justify-between">
              <p 
                className="font-plus-jakarta font-medium leading-6 text-[#2d2923] dark:text-[#fdfdfd] tracking-[-0.2px]"
                
              >
                <span className="text-[18px]">{Math.abs(dealSpread).toFixed(1)}</span>
                <span className="text-[18px]"> </span>
                <span className="text-[12px] text-[rgba(45,41,35,0.48)] dark:text-[rgba(253,253,253,0.48)]">%</span>
              </p>
              <div className="h-[28px] overflow-hidden relative w-[172px]">
                {/* Background pattern */}
                <div 
                  className="absolute h-5 right-0 top-1 w-[177px] bg-[rgba(45,41,35,0.05)] dark:bg-[rgba(253,253,253,0.05)]" 
                  style={{
                    backgroundImage: 'repeating-linear-gradient(90deg, transparent, transparent 1px, rgba(45,41,35,0.15) 1px, rgba(45,41,35,0.15) 2px)'
                  }}
                />
                {/* Progress bar */}
                <div 
                  className="absolute bg-[#e3fd5a] h-5 left-0 top-1"
                  style={{ width: `${dealSpreadWidth}%` }}
                />
              </div>
            </div>
            <p 
              className="font-plus-jakarta font-normal leading-3 text-[11px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)] whitespace-pre-wrap"
              
            >
              Difference between buy and sell volume
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

